import { useState } from 'react';
import { atHomeClaimAlert, atHomeCloseAlert, type AtHomeAlertTier, type AtHomeOutcome } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { OpCtx } from './OperatorHome';
import { Act, chip16, D, S } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// Alerts (design T11834-11932, hOpAlerts): one owner per alert, claim it, then say what
// happened. Reads and writes the PROPOSED GET /athome/alerts, POST .../claim and .../close.

const SEV: Record<AtHomeAlertTier, [string, string, string, number]> = {
  urgent: ['Urgent', '#B3261E', 'call now', 5],
  sameday: ['Same day', '#5B6B7C', 'before end of shift', 120],
  confirm: ['Confirm', '#264C7E', 'repeat or device check', 480],
};
const ORD: AtHomeAlertTier[] = ['urgent', 'sameday', 'confirm'];
const btn = (tone: 'claim' | 'danger' | 'plain') =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:24px;padding:0 9px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (tone === 'claim' ? 'color:#FCFAF4;background:#0F6B63;' : tone === 'danger' ? 'color:#B3261E;border:1px solid rgba(179,38,30,.38);' : 'color:#0F6B63;border:1px solid rgba(15,107,99,.36);');
const bar = (w: number, col: string) => 'display:block;height:100%;width:' + w + '%;border-radius:2px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;background:' + col + ';';
const HEAD = 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#22272E;';
const META = 'flex:1 1 100px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;';

/** "47m 00s", "3h 10m 00s" (the design's live clock, from when the alert opened). */
export function liveAge(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600);
  const mm = Math.floor((t % 3600) / 60);
  const ss = t % 60;
  return (h ? `${h}h ` : '') + (h || mm ? `${h && mm < 10 ? '0' : ''}${mm}m ` : '') + `${ss < 10 ? '0' : ''}${ss}s`;
}

export function OpAlerts({ c }: { c: OpCtx }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const [sev, setSev] = useState<'all' | AtHomeAlertTier>('all');
  const [err, setErr] = useState<string | null>(null);
  const data = c.o.alerts.data;
  const alerts = data?.alerts ?? [];
  const now = Date.now();
  const age = (a: (typeof alerts)[number]) => now - Date.parse(a.openedAt);
  const lateOnly = c.lateOnly;
  const isLate = (a: (typeof alerts)[number]) => !a.owner && age(a) > 15 * 60_000;
  const list = alerts.filter((a) => (lateOnly ? isLate(a) : sev === 'all' || a.tier === sev)).slice().sort((a, b) => ORD.indexOf(a.tier) - ORD.indexOf(b.tier) || age(b) - age(a));
  const unowned = alerts.filter((a) => !a.owner);
  const closed = data?.closedToday ?? { 'confirmed ok': 0, clinician: 0, escalated: 0 };
  const closedTot = closed['confirmed ok'] + closed.clinician + closed.escalated;
  const members = c.o.caseload.data?.members ?? [];

  const run = async (p: Promise<unknown>) => {
    setErr(null);
    try {
      await p;
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      c.o.alerts.reload();
    }
  };
  const claim = (id: string) => !c.readOnly && void run(atHomeClaimAlert(client, { params: { id } }));
  const close = (id: string, outcome: AtHomeOutcome, note: string) => !c.readOnly && void run(atHomeCloseAlert(client, { params: { id }, body: { outcome, note } }));

  return (
    <D css="display:flex;flex-direction:column;gap:9px;min-width:0;">
      <D css="display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;row-gap:6px;min-width:0;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.12);">
        <S css={'flex:0 0 auto;font-size:21px;font-weight:700;letter-spacing:-.025em;line-height:1.15;color:' + (unowned.length ? '#B3261E' : '#12503C') + ';'}>
          {!data ? 'Alerts are not connected yet' : unowned.length ? `${unowned.length}${unowned.length === 1 ? ' alert has nobody on it' : ' alerts have nobody on them'}` : 'Every open alert has an owner'}
        </S>
        <S css="flex:1 1 200px;min-width:0;font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;">
          {data
            ? `${alerts.length} open · ${closedTot} closed today · unowned past 15 minutes is the failure mode in remote care, not a missed reading.`
            : plain('Waiting on GET /athome/alerts. Nothing here means not wired, never all clear.')}
        </S>
        {data &&
          (['all', ...ORD] as const).map((k) => {
            const n = k === 'all' ? alerts.length : alerts.filter((a) => a.tier === k).length;
            const col = k === 'all' ? '#22272E' : SEV[k][1];
            return (
              <Act
                key={k}
                css={'display:flex;align-items:baseline;gap:8px;padding:4px 10px;border-radius:8px;min-width:0;text-decoration:none;color:inherit;transition:background .18s ease;border-left:3px solid ' + col + ';' + (sev === k ? 'background:rgba(34,39,46,.08);' : '')}
                onClick={() => {
                  c.setLateOnly(false);
                  setSev(k);
                }}
              >
                <S css={'font-size:21px;font-weight:700;letter-spacing:-.03em;line-height:1;font-variant-numeric:tabular-nums;color:' + col + ';'}>{String(n)}</S>
                <S css="display:block;min-width:0;">
                  <S css="display:block;font-size:14px;font-weight:700;white-space:nowrap;">{k === 'all' ? 'All open' : SEV[k][0]}</S>
                  <S css="display:block;font-size:14px;color:#54606C;white-space:nowrap;">{k === 'all' ? 'across every queue' : SEV[k][2]}</S>
                </S>
              </Act>
            );
          })}
      </D>
      {lateOnly && (
        <D css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;font-size:14px;color:#3C454F;min-width:0;" testId="alerts-late-only">
          <S css="flex:0 1 auto;min-width:0;">{`Showing the ${list.length} unowned past 15 minutes.`}</S>
          <Act css={btn('plain')} onClick={() => c.setLateOnly(false)}>Show all</Act>
        </D>
      )}
      {err && <D css="font-size:14px;color:#B3261E;" role="alert">{`Not sent: ${err}`}</D>}
      <D css="min-width:0;">
        {list.map((a, i) => {
          const s = SEV[a.tier];
          const mins = age(a) / 60_000;
          const late = !a.owner && mins > 15;
          const m = members.find((x) => x.residentId === a.residentId);
          return (
            <D key={a.id} testId="alert-row" css={'display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:5px 9px;border-radius:8px;min-width:0;animation:slideIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 35 + 'ms;border-left:3px solid ' + s[1] + ';background:' + (late ? 'rgba(179,38,30,.06)' : 'rgba(34,39,46,.035)') + ';'}>
              <S css={'flex:0 0 8px;width:8px;height:8px;border-radius:' + (a.tier === 'urgent' ? '2px' : a.tier === 'sameday' ? '50%' : '1px') + ';background:' + s[1] + ';' + (late ? 'animation:pRed 1.6s ease-out infinite;' : '')} />
              <S css={chip16(s[1], s[1] + '1F')}>{s[0]}</S>
              <Act css="flex:0 0 130px;min-width:0;font-size:16px;font-weight:700;letter-spacing:-.01em;overflow-wrap:break-word;text-decoration:none;color:inherit;" onClick={() => m && c.viewAs({ residentId: m.residentId, name: m.name })}>
                {a.member}
              </Act>
              <S css="flex:3 1 240px;min-width:0;font-size:14px;color:#22272E;line-height:1.35;text-wrap:pretty;">{a.what}</S>
              <S css="flex:0 0 62px;min-width:0;">
                <S css="display:block;height:4px;border-radius:2px;background:rgba(34,39,46,.1);overflow:hidden;">
                  <S css={bar(Math.min(100, Math.round((mins / s[3]) * 100)), mins > s[3] ? '#B3261E' : mins > s[3] * 0.6 ? '#5B6B7C' : '#2F8A6F')} />
                </S>
                <S css="display:block;font-size:14px;color:#54606C;margin-top:2px;white-space:nowrap;">{`target ${s[3] < 60 ? `${s[3]}m` : `${Math.round(s[3] / 60)}h`}`}</S>
              </S>
              <S css="flex:0 0 auto;text-align:right;">
                <S css={'display:block;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1;white-space:nowrap;color:' + (late || a.tier === 'urgent' ? '#B3261E' : '#22272E') + ';'}>{liveAge(age(a))}</S>
                <S css="display:block;font-size:14px;letter-spacing:.08em;text-transform:uppercase;white-space:nowrap;color:#54606C;margin-top:2px;">elapsed</S>
              </S>
              <S css={chip16(a.owner ? '#3C454F' : '#B3261E', a.owner ? 'rgba(34,39,46,.08)' : 'rgba(179,38,30,.12)')}>{a.owner ?? 'UNOWNED'}</S>
              {!a.owner && (
                <Act css={btn('claim')} onClick={() => claim(a.id)} disabled={c.readOnly} reason={c.reason}>
                  Claim
                </Act>
              )}
              {a.owner && (
                <S css="flex:0 0 auto;display:flex;align-items:center;gap:5px;flex-wrap:wrap;">
                  <Act css={btn('plain')} onClick={() => close(a.id, 'confirmed ok', 'checked, nothing needed')} disabled={c.readOnly} reason={c.reason}>
                    Resolve
                  </Act>
                  <Act css={btn('plain')} onClick={() => close(a.id, 'clinician', 'same-day nurse review booked')} disabled={c.readOnly} reason={c.reason}>
                    Clinician
                  </Act>
                  <Act css={btn('danger')} onClick={() => close(a.id, 'escalated', 'escalated up the ladder')} disabled={c.readOnly} reason={c.reason}>
                    Escalate
                  </Act>
                </S>
              )}
            </D>
          );
        })}
        {data && list.length === 0 && (
          <D css="display:flex;align-items:center;gap:9px;padding:8px 12px;border-radius:9px;background:rgba(47,138,111,.09);border:1px solid rgba(47,138,111,.28);font-size:16px;font-weight:600;color:#12503C;min-width:0;flex-wrap:wrap;">
            <S css="flex:0 0 auto;width:8px;height:8px;border-radius:2px;background:#2F8A6F;animation:breathe 2.6s ease-in-out infinite;" />
            Nothing open in this queue.
          </D>
        )}
      </D>
      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:10px 26px;min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);align-items:start;">
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;"><S css={HEAD}>Who is on</S><S css={META}>who is holding what right now</S></D>
          {[...(data?.team ?? []).map((n) => {
            const held = alerts.filter((a) => a.owner === n).length;
            return { k: n, v: held ? `${held} open` : 'clear', color: held > 1 ? '#4A5866' : held ? '#22272E' : '#1F6B52', dot: held > 1 ? '#5B6B7C' : held ? '#2F5C97' : '#2F8A6F', w: Math.min(100, held * 34), b: held > 1 ? '#5B6B7C' : '#2F5C97', pulse: false };
          }), ...(data ? [{ k: 'Unowned', v: unowned.length ? `${unowned.length} waiting` : 'none', color: unowned.length ? '#B3261E' : '#1F6B52', dot: unowned.length ? '#B3261E' : '#2F8A6F', w: Math.min(100, unowned.length * 34), b: '#B3261E', pulse: unowned.length > 0 }] : [])].map((t) => (
            <S key={t.k} css="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;">
              <S css={'flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:' + t.dot + ';' + (t.pulse ? 'animation:pRed 1.8s ease-out infinite;' : '')} />
              <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{t.k}</S>
              <S css="flex:0 0 54px;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={bar(t.w, t.b)} /></S>
              <S css={'flex:0 0 auto;font-size:14px;font-weight:700;white-space:nowrap;color:' + t.color + ';'}>{t.v}</S>
            </S>
          ))}
        </D>
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;"><S css={HEAD}>Time to a human</S><S css={META}>time to the first human {'·'} today</S></D>
          {(data?.firstHuman ?? []).map((r) => {
            // No alert in this tier reached a person today: said, not drawn as "nullm".
            const pct = r.minutes == null ? 0 : Math.round((r.minutes / r.targetMinutes) * 100);
            const fmt = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`);
            return (
              <S key={r.tier} css="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;">
                <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{r.tier}</S>
                <S css="flex:0 0 54px;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={bar(pct, pct > 75 ? '#5B6B7C' : '#2F8A6F')} /></S>
                <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + (r.minutes == null ? '#54606C' : pct > 75 ? '#4A5866' : '#1F6B52') + ';'}>{r.minutes == null ? 'none today' : fmt(r.minutes)}</S>
                <S css="flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;">{`target ${fmt(r.targetMinutes)}`}</S>
              </S>
            );
          })}
        </D>
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;"><S css={HEAD}>How they closed</S><S css={META}>{data ? `${closedTot} closed today` : ''}</S></D>
          {data &&
            ([['Confirmed ok', closed['confirmed ok'], '#2F8A6F'], ['Clinician booked', closed.clinician, '#2F5C97'], ['Escalated', closed.escalated, '#B3261E']] as const).map(([k, v, col]) => (
              <S key={k} css="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;">
                <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{k}</S>
                <S css="flex:0 0 54px;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={bar(Math.round((v / Math.max(1, closedTot)) * 100), col)} /></S>
                <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + col + ';'}>{String(v)}</S>
                <S css="flex:0 0 26px;text-align:right;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;">{`${Math.round((v / Math.max(1, closedTot)) * 100)}%`}</S>
              </S>
            ))}
        </D>
      </D>
      <D css="font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;">{`One owner per alert. Claim it, then say what happened, resolve, book a clinician, or escalate. Closing writes the disposition to the sealed ledger under your name. Elapsed clocks are live.${data && !data.real ? ' SAMPLE until the member feed is wired.' : ''}`}</D>
    </D>
  );
}
